import {
  Directive,
  EmbeddedViewRef,
  inject,
  input,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, merge, switchMap } from 'rxjs';
import { UnleashFlags, UnleashService } from '.';

@Directive({
  selector: '[appFeatureFlagEnabled],[appFeatureFlagDisabled]',
})
export class FeatureFlagDirective {
  readonly #featureFlagService = inject(UnleashService);

  readonly #templateRef = inject(TemplateRef);

  readonly #viewContainer = inject(ViewContainerRef);

  readonly appFeatureFlagEnabled = input<UnleashFlags>();

  readonly appFeatureFlagDisabled = input<UnleashFlags>();

  #embeddedView?: EmbeddedViewRef<unknown>;

  constructor() {
    const enabled$ = toObservable(this.appFeatureFlagEnabled).pipe(
      filter((v): v is UnleashFlags => v !== undefined),
      map((flag) => ({ flag, shouldEnable: true })),
    );
    const disabled$ = toObservable(this.appFeatureFlagDisabled).pipe(
      filter((v): v is UnleashFlags => v !== undefined),
      map((flag) => ({ flag, shouldEnable: false })),
    );

    merge(enabled$, disabled$)
      .pipe(
        distinctUntilChanged(
          (a, b) => a.flag === b.flag && a.shouldEnable === b.shouldEnable,
        ),
        switchMap(({ flag, shouldEnable }) =>
          this.#featureFlagService
            .isEnabled$(flag)
            .pipe(map((isEnabled) => ({ isEnabled, shouldEnable }))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ isEnabled, shouldEnable }) => {
        if (isEnabled === shouldEnable && !this.#embeddedView) {
          this.#embeddedView = this.#viewContainer.createEmbeddedView(
            this.#templateRef,
          );
        } else if (isEnabled !== shouldEnable && this.#embeddedView) {
          this.#embeddedView.destroy();
          this.#embeddedView = undefined;
        }
      });
  }
}
